import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { MapPin, Filter, X, Search, Star, Award } from 'lucide-react';
import { FACILITY_TYPES, getFacilityTypeLabel } from '@/data/facilityTypes';
import { FacilitiesPageSEO } from '@/components/SEOHead';
import { sportBranches, facilityFeatures } from '@/data/turkeyData';

const API = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/(api)?\/?$/, "")}/api`;

const FacilitiesPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [facilities, setFacilities] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  
  // Filters
  const [city, setCity] = useState(searchParams.get('city') || '');
  const [district, setDistrict] = useState('');
  const [facilityType, setFacilityType] = useState(searchParams.get('type') || '');
  const [branch, setBranch] = useState('');
  const [priceRange, setPriceRange] = useState([0, 1000]);

  useEffect(() => {
    fetchFacilities();
  }, []);

  const fetchFacilities = async () => {
    try {
      const params = new URLSearchParams();
      if (city) params.set('city', city);
      if (district) params.set('district', district);
      if (facilityType) params.set('facility_type', facilityType);
      if (branch) params.set('branch', branch);
      if (priceRange[0] > 0) params.set('min_price', priceRange[0]);
      if (priceRange[1] < 1000) params.set('max_price', priceRange[1]);

      const response = await axios.get(`${API}/facilities?${params.toString()}`);
      setFacilities(response.data);
    } catch (error) {
      console.error('Error fetching facilities:', error);
    }
  };

  const handleFilter = () => {
    fetchFacilities();
    setShowFilters(false);
  };

  const clearFilters = () => {
    setCity('');
    setDistrict('');
    setFacilityType('');
    setBranch('');
    setPriceRange([0, 1000]);
    setSearchParams({});
  };

  const facilityTypes = FACILITY_TYPES;

  // Helper function to get feature display with emoji
  const getFeatureDisplay = (featureId) => {
    const feature = facilityFeatures.find(f => f.id === featureId);
    if (feature) {
      return `${feature.icon} ${feature.label}`;
    }
    // Fallback for old format
    return featureId.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* SEO Head */}
      <FacilitiesPageSEO 
        facilities={facilities} 
        city={city} 
        district={district} 
        facilityType={facilityType}
      />
      
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between gap-4">
            <h1 className="text-2xl font-bold text-slate-900">Tesisler</h1>
            <Button 
              variant="outline" 
              onClick={() => setShowFilters(!showFilters)}
              className="md:hidden"
              data-testid="toggle-filters-btn"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filtrele
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Filters Sidebar */}
          <aside className={`
            md:w-72 flex-shrink-0
            ${showFilters ? 'fixed inset-0 z-50 bg-white p-6 overflow-auto' : 'hidden md:block'}
          `}>
            {showFilters && (
              <div className="flex items-center justify-between mb-6 md:hidden">
                <h2 className="text-xl font-bold">Filtreler</h2>
                <Button variant="ghost" onClick={() => setShowFilters(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
            )}

            <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-6" data-testid="filters-panel">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Şehir</label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Şehir ara"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="pl-10"
                    data-testid="filter-city-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">İlçe</label>
                <Input
                  placeholder="İlçe ara"
                  value={district}
                  onChange={(e) => setDistrict(e.target.value)}
                  data-testid="filter-district-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Tesis Türü</label>
                <Select value={facilityType || "all"} onValueChange={(v) => setFacilityType(v === "all" ? "" : v)}>
                  <SelectTrigger data-testid="filter-type-select">
                    <SelectValue placeholder="Tüm türler" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tüm türler</SelectItem>
                    {facilityTypes.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Spor Dalı</label>
                <Select value={branch || "all"} onValueChange={(v) => setBranch(v === "all" ? "" : v)}>
                  <SelectTrigger data-testid="filter-branch-select">
                    <SelectValue placeholder="Tüm branşlar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tüm branşlar</SelectItem>
                    {sportBranches.map(b => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.icon} {b.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Fiyat Aralığı: {priceRange[0]} - {priceRange[1]} ₺
                </label>
                <Slider
                  value={priceRange}
                  onValueChange={setPriceRange}
                  min={0}
                  max={1000}
                  step={50}
                  className="mt-4"
                  data-testid="filter-price-slider"
                />
              </div>

              <div className="flex gap-2 pt-4">
                <Button 
                  onClick={handleFilter}
                  className="flex-1 bg-emerald-700 hover:bg-emerald-800"
                  data-testid="apply-filters-btn"
                >
                  <Search className="w-4 h-4 mr-2" />
                  Filtrele
                </Button>
                <Button 
                  variant="outline" 
                  onClick={clearFilters}
                  data-testid="clear-filters-btn"
                >
                  Temizle
                </Button>
              </div>
            </div>
          </aside>

          {/* Results */}
          <main className="flex-1">
            {facilities.length > 0 ? (
              <>
                <p className="text-slate-600 mb-4" data-testid="results-count">
                  {facilities.length} tesis bulundu
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {facilities.map((facility, index) => (
                    <Card 
                      key={facility.facility_id} 
                      className="group overflow-hidden cursor-pointer card-hover bg-white"
                      onClick={() => navigate(`/tesis/${facility.facility_id}`)}
                      data-testid={`facility-card-${index}`}
                    >
                      <div className="aspect-video relative overflow-hidden">
                        <img
                          src={facility.images?.[0] || (facility.facility_type === 'hali_saha' 
                            ? 'https://images.pexels.com/photos/47730/the-ball-stadion-football-the-pitch-47730.jpeg'
                            : 'https://images.unsplash.com/photo-1546519638-68e109498ffc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxpbmRvb3IlMjBiYXNrZXRiYWxsJTIwY291cnR8ZW58MHx8fHwxNzY2NDEyMDIzfDA&ixlib=rb-4.1.0&q=85')}
                          alt={facility.name}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                        <div className="absolute top-3 left-3 flex flex-col gap-2">
                          {facility.is_partner ? (
                            <Badge className="bg-amber-500 text-white shadow-lg flex items-center gap-1">
                              <Award className="w-3 h-3" />
                              Partner
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="bg-slate-100 text-slate-600 shadow-sm opacity-90">
                              Partner Değil
                            </Badge>
                          )}
                        </div>
                        <div className="absolute top-3 right-3">
                          <span className="price-tag">
                            {facility.price_per_hour} ₺/saat
                          </span>
                        </div>
                      </div>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-1">
                          <h3 className="font-bold text-lg text-slate-900 group-hover:text-emerald-700 transition-colors">
                            {facility.name}
                          </h3>
                          {facility.average_rating > 0 && (
                            <div className="flex items-center gap-1 bg-yellow-50 px-2 py-1 rounded-lg">
                              <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                              <span className="text-sm font-bold text-yellow-700">{facility.average_rating}</span>
                              <span className="text-xs text-yellow-600">({facility.review_count || 0})</span>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center text-slate-500 text-sm mb-3">
                          <MapPin className="w-4 h-4 mr-1" />
                          {facility.district}, {facility.city}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <span className="inline-flex items-center px-2 py-1 rounded-md bg-slate-100 text-slate-700 text-xs">
                            {getFacilityTypeLabel(facility.facility_type)}
                          </span>
                          {facility.features?.slice(0, 2).map((feature, idx) => (
                            <span key={idx} className="inline-flex items-center px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 text-xs">
                              {getFeatureDisplay(feature)}
                            </span>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-16 bg-white rounded-xl border border-slate-200" data-testid="no-results">
                <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Tesis Bulunamadı</h3>
                <p className="text-slate-600 mb-4">
                  Arama kriterlerinize uygun tesis bulunamadı.
                </p>
                <Button 
                  variant="outline" 
                  onClick={clearFilters}
                >
                  Filtreleri Temizle
                </Button>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default FacilitiesPage;
